/**
 * Pure business rules for the client portal (no I/O). Re-exported as `@buildbee/domain` -> `portal`
 * and importable as `@buildbee/domain/portal`.
 */
import {
  PORTAL_CAPABILITIES,
  type ChangeOrderStatus,
  type InvoiceStatus,
  type PaymentStatus,
  type PortalCapability,
  type PortalPaymentMethod,
  type SelectionOption,
  type SelectionStatus,
} from '@buildbee/contracts';

// ---------------------------------------------------------------------------
// Capabilities ("what is shared")
// ---------------------------------------------------------------------------

/** Groups shown to the team when choosing what a client can see/do on a project. */
export const CAPABILITY_GROUPS: { key: string; label: string; description: string; capabilities: PortalCapability[] }[] = [
  { key: 'progress', label: 'Progress', description: 'Project status and percent complete', capabilities: ['view_progress'] },
  { key: 'photos', label: 'Progress photos', description: 'Photos marked as shared with the client', capabilities: ['view_photos'] },
  { key: 'schedule', label: 'Schedule summary', description: 'Milestones and client-visible schedule items', capabilities: ['view_schedule'] },
  { key: 'documents', label: 'Documents', description: 'Documents marked as shared, with download', capabilities: ['view_documents'] },
  { key: 'selections', label: 'Selections', description: 'Choose and approve finishes and options', capabilities: ['make_selections'] },
  { key: 'change_orders', label: 'Change orders', description: 'Review, approve and e-sign change orders', capabilities: ['approve_change_orders'] },
  { key: 'invoices', label: 'Invoices', description: 'See invoices and balances', capabilities: ['view_invoices'] },
  { key: 'payments', label: 'Online payments', description: 'Pay invoices by card or bank transfer', capabilities: ['pay_invoices'] },
  { key: 'messages', label: 'Messages', description: 'Message thread with the project team', capabilities: ['message'] },
];

export const CAPABILITY_LABELS: Record<PortalCapability, string> = {
  view_progress: 'Progress',
  view_schedule: 'Schedule',
  view_documents: 'Documents',
  view_photos: 'Photos',
  make_selections: 'Selections',
  approve_change_orders: 'Change orders',
  view_invoices: 'Invoices',
  pay_invoices: 'Payments',
  message: 'Messages',
};

/** Capabilities that imply others (paying needs seeing the invoice). */
const IMPLIED: Partial<Record<PortalCapability, PortalCapability[]>> = {
  pay_invoices: ['view_invoices'],
};

/** Dedupe, add implied capabilities, and return them in canonical order. */
export function normalizeCapabilities(input: readonly PortalCapability[]): PortalCapability[] {
  const set = new Set<PortalCapability>();
  for (const c of input) {
    set.add(c);
    for (const implied of IMPLIED[c] ?? []) set.add(implied);
  }
  return PORTAL_CAPABILITIES.filter((c) => set.has(c));
}

// ---------------------------------------------------------------------------
// Schedule summary
// ---------------------------------------------------------------------------

export type ScheduleTaskLike = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'not_started' | 'in_progress' | 'blocked' | 'done';
  percentComplete: number;
  isMilestone: boolean;
  parentId?: string | null;
};

export type ScheduleSummary = {
  startDate: string | null;
  endDate: string | null;
  /** Duration-weighted percent complete over client-visible tasks (0-100). */
  percentComplete: number;
  daysRemaining: number | null;
  current: ScheduleTaskLike[];
  upcoming: ScheduleTaskLike[];
  milestones: (ScheduleTaskLike & { state: 'done' | 'upcoming' | 'late' })[];
  completedCount: number;
  totalCount: number;
};

const DAY_MS = 86_400_000;

function dayNumber(d: string): number {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  return Math.floor(Date.UTC(y, m - 1, day) / DAY_MS);
}

/** Today's `YYYY-MM-DD` in UTC (callers in a timezone pass their own date string). */
export function todayIso(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * Client-facing schedule summary: what is happening now, what is next, and the milestones.
 * `tasks` must already be filtered to client-visible rows.
 */
export function summarizeSchedule(tasks: readonly ScheduleTaskLike[], today: string, upcomingLimit = 5): ScheduleSummary {
  if (tasks.length === 0) {
    return { startDate: null, endDate: null, percentComplete: 0, daysRemaining: null, current: [], upcoming: [], milestones: [], completedCount: 0, totalCount: 0 };
  }
  const t = dayNumber(today);
  const sorted = [...tasks].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.name.localeCompare(b.name));
  const startDate = sorted[0]!.startDate;
  const endDate = sorted.reduce((max, x) => (x.endDate > max ? x.endDate : max), sorted[0]!.endDate);

  let weight = 0;
  let done = 0;
  for (const task of sorted) {
    if (task.isMilestone) continue;
    const w = Math.max(1, dayNumber(task.endDate) - dayNumber(task.startDate) + 1);
    const pct = task.status === 'done' ? 100 : Math.min(100, Math.max(0, task.percentComplete));
    weight += w;
    done += (w * pct) / 100;
  }
  const workTasks = sorted.filter((x) => !x.isMilestone);
  const percentComplete =
    weight > 0 ? Math.round((done / weight) * 100) : Math.round((sorted.filter((x) => x.status === 'done').length / sorted.length) * 100);

  const current = workTasks.filter(
    (x) => x.status !== 'done' && (x.status === 'in_progress' || (dayNumber(x.startDate) <= t && dayNumber(x.endDate) >= t)),
  );
  const upcoming = workTasks.filter((x) => x.status === 'not_started' && dayNumber(x.startDate) > t).slice(0, upcomingLimit);
  const milestones = sorted
    .filter((x) => x.isMilestone)
    .map((m) => ({
      ...m,
      state: (m.status === 'done' ? 'done' : dayNumber(m.endDate) < t ? 'late' : 'upcoming') as 'done' | 'upcoming' | 'late',
    }));

  return {
    startDate,
    endDate,
    percentComplete,
    daysRemaining: Math.max(0, dayNumber(endDate) - t),
    current,
    upcoming,
    milestones,
    completedCount: sorted.filter((x) => x.status === 'done').length,
    totalCount: sorted.length,
  };
}

// ---------------------------------------------------------------------------
// Change orders
// ---------------------------------------------------------------------------

/** Statuses a client can ever see (drafts and internal reviews stay hidden; void is hidden too). */
export const CLIENT_VISIBLE_CHANGE_ORDER_STATUSES: readonly ChangeOrderStatus[] = ['sent', 'approved', 'rejected'];

export function isChangeOrderVisibleToClient(co: { status: ChangeOrderStatus; visibleToClient: boolean; archivedAt?: Date | null }): boolean {
  return co.visibleToClient && !co.archivedAt && CLIENT_VISIBLE_CHANGE_ORDER_STATUSES.includes(co.status);
}

/** Only change orders awaiting the client can be approved or declined. */
export function canClientRespondToChangeOrder(co: { status: ChangeOrderStatus; visibleToClient: boolean; archivedAt?: Date | null }): boolean {
  return isChangeOrderVisibleToClient(co) && co.status === 'sent';
}

// ---------------------------------------------------------------------------
// Signatures
// ---------------------------------------------------------------------------

export type SignatureInput = { signerName: string; imageDataUrl?: string; typed: boolean };
export type SignatureRecord = {
  signerName: string;
  signerEmail?: string;
  imageDataUrl?: string;
  typed: boolean;
  signedAt: Date;
  ip?: string;
  userAgent?: string;
};

export type SignatureCheck = { ok: true } | { ok: false; reason: string };

/** A drawn signature needs an image; a typed one needs a name that looks like a name. */
export function validateSignature(sig: SignatureInput): SignatureCheck {
  const name = sig.signerName.trim();
  if (name.length < 2) return { ok: false, reason: 'Enter your full name to sign' };
  if (!sig.typed && !sig.imageDataUrl) return { ok: false, reason: 'Draw your signature or choose to type it' };
  if (sig.imageDataUrl && !sig.imageDataUrl.startsWith('data:image/png;base64,')) {
    return { ok: false, reason: 'Signature must be a PNG image' };
  }
  return { ok: true };
}

/** Build the stored signature (evidence: who, when, from where). */
export function buildSignature(
  sig: SignatureInput,
  evidence: { email?: string | null; ip?: string | null; userAgent?: string | null; now?: Date },
): SignatureRecord {
  const out: SignatureRecord = {
    signerName: sig.signerName.trim(),
    typed: sig.typed,
    signedAt: evidence.now ?? new Date(),
  };
  if (evidence.email) out.signerEmail = evidence.email.toLowerCase();
  if (sig.imageDataUrl && !sig.typed) out.imageDataUrl = sig.imageDataUrl;
  if (evidence.ip) out.ip = evidence.ip.slice(0, 64);
  if (evidence.userAgent) out.userAgent = evidence.userAgent.slice(0, 512);
  return out;
}

// ---------------------------------------------------------------------------
// Selections
// ---------------------------------------------------------------------------

/** Statuses visible to clients (drafts are still being prepared). */
export const CLIENT_VISIBLE_SELECTION_STATUSES: readonly SelectionStatus[] = ['pending_client', 'selected', 'approved', 'ordered', 'installed'];

export function canClientChooseSelection(status: SelectionStatus): boolean {
  return status === 'pending_client' || status === 'selected';
}

export function canClientApproveSelection(sel: { status: SelectionStatus; chosenOptionId: string | null }): boolean {
  return sel.status === 'selected' && !!sel.chosenOptionId;
}

export type SelectionPricing = {
  optionPriceCents: number;
  allowanceCents: number;
  /** Positive: the client pays more than the allowance. Negative: a credit. */
  differenceCents: number;
};

export function selectionPricing(sel: { allowanceCents: number; options: readonly Pick<SelectionOption, 'id' | 'priceCents'>[] }, optionId: string | null): SelectionPricing | null {
  if (!optionId) return null;
  const option = sel.options.find((o) => o.id === optionId);
  if (!option) return null;
  return { optionPriceCents: option.priceCents, allowanceCents: sel.allowanceCents, differenceCents: option.priceCents - sel.allowanceCents };
}

/** Option ids must be unique within a selection. Returns duplicates (empty when valid). */
export function duplicateOptionIds(options: readonly { id: string }[]): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const o of options) {
    if (seen.has(o.id)) dupes.add(o.id);
    seen.add(o.id);
  }
  return [...dupes];
}

// ---------------------------------------------------------------------------
// Invoices + payments
// ---------------------------------------------------------------------------

/** Statuses a client sees; drafts and voided invoices stay hidden. */
export const CLIENT_VISIBLE_INVOICE_STATUSES: readonly InvoiceStatus[] = ['sent', 'viewed', 'partially_paid', 'paid', 'overdue'];

export function isInvoiceVisibleToClient(inv: { status: InvoiceStatus; archivedAt?: Date | null }): boolean {
  return !inv.archivedAt && CLIENT_VISIBLE_INVOICE_STATUSES.includes(inv.status);
}

/** Status the client sees: open invoices past due read as overdue. */
export function invoiceDisplayStatus(inv: { status: InvoiceStatus; dueDate: string; balanceCents: number }, today: string): InvoiceStatus {
  if (inv.status === 'paid' || inv.status === 'void' || inv.status === 'draft') return inv.status;
  if (inv.balanceCents > 0 && inv.dueDate < today) return 'overdue';
  return inv.status;
}

/** Stripe's minimum charge: 50 cents in both CAD and USD. */
export const MIN_PAYMENT_CENTS = 50;

export type PayableCheck = { ok: true; amountCents: number } | { ok: false; reason: string };

/** Validate a (possibly partial) payment amount against the invoice balance. */
export function payableAmount(
  inv: { status: InvoiceStatus; balanceCents: number; archivedAt?: Date | null },
  requestedCents?: number,
): PayableCheck {
  if (!isInvoiceVisibleToClient(inv)) return { ok: false, reason: 'This invoice cannot be paid online' };
  if (inv.status === 'paid' || inv.balanceCents <= 0) return { ok: false, reason: 'This invoice is already paid' };
  const amount = requestedCents ?? inv.balanceCents;
  if (!Number.isInteger(amount) || amount <= 0) return { ok: false, reason: 'Enter a valid amount' };
  if (amount > inv.balanceCents) return { ok: false, reason: 'Amount is more than the balance due' };
  if (amount < MIN_PAYMENT_CENTS) return { ok: false, reason: 'The minimum online payment is $0.50' };
  return { ok: true, amountCents: amount };
}

/** Card, US bank account (ACH, USD only) or Canadian pre-authorized debit (ACSS, CAD only). */
export type StripeMethodType = 'card' | 'us_bank_account' | 'acss_debit';

/** Stripe's bank-debit method for a currency: ACSS pre-authorized debit for CAD, ACH for USD. */
export function bankDebitMethodFor(currency: string): StripeMethodType | null {
  const c = currency.toLowerCase();
  if (c === 'cad') return 'acss_debit';
  if (c === 'usd') return 'us_bank_account';
  return null;
}

/**
 * Payment method types for Checkout given the invoice's allowed methods, the client's choice and
 * the charge currency (lowercase or uppercase ISO code; the bank method depends on it). The
 * portal's "ach" choice means "pay from a bank account" in either country.
 */
export function checkoutMethodTypes(
  allowed: { allowCard: boolean; allowAch: boolean },
  choice: PortalPaymentMethod,
  currency: string,
): StripeMethodType[] | { error: string } {
  const out: StripeMethodType[] = [];
  const bank = bankDebitMethodFor(currency);
  if (allowed.allowCard && (choice === 'card' || choice === 'any')) out.push('card');
  if (allowed.allowAch && bank && (choice === 'ach' || choice === 'any')) out.push(bank);
  if (out.length === 0) {
    if (choice === 'card') return { error: 'Card payments are not accepted for this invoice' };
    if (choice === 'ach') {
      return allowed.allowAch && !bank
        ? { error: `Bank payments are not available in ${currency.toUpperCase()}` }
        : { error: 'Bank transfers are not accepted for this invoice' };
    }
    return { error: 'Online payment is not enabled for this invoice' };
  }
  return out;
}

const PAYMENT_RANK: Record<PaymentStatus, number> = {
  pending: 0,
  processing: 1,
  succeeded: 2,
  failed: 2,
  cancelled: 2,
  partially_refunded: 3,
  refunded: 4,
};

/**
 * Whether a payment may move from `from` to `to` (webhooks arrive out of order and are retried).
 * Forward-only, except a succeeded bank payment can still fail (ACH return).
 */
export function canTransitionPayment(from: PaymentStatus | null, to: PaymentStatus): boolean {
  if (from === null) return true;
  if (from === to) return false;
  if (from === 'succeeded' && to === 'failed') return true;
  if (from === 'failed' && to === 'succeeded') return true;
  return PAYMENT_RANK[to] > PAYMENT_RANK[from];
}

export type InvoiceMoney = { status: InvoiceStatus; totalCents: number; amountPaidCents: number; paidAt: Date | null };

/**
 * New invoice totals after a payment changes status. Only transitions into or out of `succeeded`
 * move money. Returns null when nothing changes.
 */
export function applyPaymentToInvoice(
  inv: InvoiceMoney,
  payment: { amountCents: number; from: PaymentStatus | null; to: PaymentStatus },
  now = new Date(),
): { amountPaidCents: number; balanceCents: number; status: InvoiceStatus; paidAt: Date | null } | null {
  const wasCounted = payment.from === 'succeeded';
  const isCounted = payment.to === 'succeeded';
  if (wasCounted === isCounted) return null;
  const delta = isCounted ? payment.amountCents : -payment.amountCents;
  const amountPaidCents = Math.max(0, inv.amountPaidCents + delta);
  const balanceCents = inv.totalCents - amountPaidCents;
  let status: InvoiceStatus = inv.status;
  if (inv.status !== 'void' && inv.status !== 'draft') {
    if (balanceCents <= 0) status = 'paid';
    else if (amountPaidCents > 0) status = 'partially_paid';
    else status = inv.status === 'paid' || inv.status === 'partially_paid' ? 'sent' : inv.status;
  }
  return { amountPaidCents, balanceCents, status, paidAt: balanceCents <= 0 ? (inv.paidAt ?? now) : null };
}

/** Map a Stripe payment method type to our payment method enum. */
export function paymentMethodFromStripe(types: readonly string[] | null | undefined, fallback: 'card' | 'ach' = 'card'): 'card' | 'ach' {
  if (!types || types.length !== 1) return fallback;
  return types[0] === 'us_bank_account' || types[0] === 'acss_debit' ? 'ach' : 'card';
}

// ---------------------------------------------------------------------------
// Activity + views
// ---------------------------------------------------------------------------

export const PORTAL_ACTIONS = [
  'signed_in',
  'viewed_project',
  'viewed_invoice',
  'viewed_change_order',
  'downloaded_document',
  'chose_selection',
  'approved_selection',
  'approved_change_order',
  'declined_change_order',
  'started_payment',
  'paid_invoice',
  'payment_failed',
  'sent_message',
] as const;
export type PortalAction = (typeof PORTAL_ACTIONS)[number];

const ACTION_VERBS: Record<PortalAction, string> = {
  signed_in: 'Signed in to the client portal',
  viewed_project: 'Viewed project',
  viewed_invoice: 'Viewed invoice',
  viewed_change_order: 'Viewed change order',
  downloaded_document: 'Downloaded document',
  chose_selection: 'Chose selection',
  approved_selection: 'Approved selection',
  approved_change_order: 'Approved and signed change order',
  declined_change_order: 'Declined change order',
  started_payment: 'Started payment for invoice',
  paid_invoice: 'Paid invoice',
  payment_failed: 'Payment failed for invoice',
  sent_message: 'Sent a message',
};

/** Subject line for the CRM activity written for a portal action. */
export function portalActivitySubject(action: PortalAction, label?: string | null): string {
  const verb = ACTION_VERBS[action];
  return label ? `${verb}: ${label}` : verb;
}

/** Views are recorded at most once per window per record so the timeline stays readable. */
export const VIEW_DEDUPE_MS = 12 * 60 * 60 * 1000;

export function shouldRecordView(lastRecordedAt: Date | null | undefined, now = new Date(), windowMs = VIEW_DEDUPE_MS): boolean {
  if (!lastRecordedAt) return true;
  return now.getTime() - lastRecordedAt.getTime() >= windowMs;
}

// ---------------------------------------------------------------------------
// Links + branding
// ---------------------------------------------------------------------------

/** Accept only in-portal relative paths as post-login redirects (no open redirects). */
export function safePortalPath(path: string | null | undefined, fallback = '/portal'): string {
  if (!path) return fallback;
  if (!path.startsWith('/portal')) return fallback;
  if (path.startsWith('//') || path.includes('\\') || /[\s<>"']/.test(path) || path.includes('..')) return fallback;
  if (path.length > 512) return fallback;
  if (path !== '/portal' && !path.startsWith('/portal/') && !path.startsWith('/portal?')) return fallback;
  return path;
}

export function portalUrl(webUrl: string, slug: string, subPath = ''): string {
  const base = webUrl.replace(/\/$/, '');
  const tail = subPath ? (subPath.startsWith('/') ? subPath : `/${subPath}`) : '';
  return `${base}/portal/${encodeURIComponent(slug)}${tail}`;
}

/** Readable text color (near-black or white) on a `#RRGGBB` background, by WCAG relative luminance. */
export function contrastText(hex: string): '#111827' | '#ffffff' {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#111827';
  const n = parseInt(m[1]!, 16);
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
  // Contrast against black vs white; pick the larger.
  return (lum + 0.05) / 0.05 >= 1.05 / (lum + 0.05) ? '#111827' : '#ffffff';
}

/** Trim a message body into a one-line preview. */
export function messagePreview(body: string, max = 140): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}
